import { getApiBaseUrl as getResolvedApiBaseUrl, logApiConfig } from './api.resolver';


export const getApiBaseUrl = (): string => {
    return getResolvedApiBaseUrl();
};

// Log configuration on initialization
    if (import.meta.env.DEV) {
        logApiConfig();
}

export const API_ENDPOINTS = {
    // User Management endpoints - matches backend UserManagementController
    USERS: {
        DROPDOWN: '/api/users/utils/dropdown',
        LIST: '/api/users/list',
        GET_BY_ID: (id: string) => `/api/users/${id}/details`,
        CREATE: '/api/users',
        UPDATE: (id: string) => `/api/users/${id}`,
        DELETE: (id: string) => `/api/users/${id}`,
        METRICS: '/api/users/metrics',
        EXPORT: '/api/users/export',
        ACTIVE_USERS_FOR_REPORTING: '/api/users/reporting/active-users',
        UPDATE_STATUS: (id: string) => `/api/users/${id}/status`,
        ADMIN_PASSWORD_RESET: (id: string) => `/api/users/${id}/password`,
        CHANGE_PASSWORD: (id: string) => `/api/users/${id}/change-password`,
        BULK_DELETE: '/api/users/bulk',
        BULK_STATUS_UPDATE: '/api/users/bulk/status',
        // User Course Progress specific endpoints
        GET_DETAILS: (userId: string) => `/api/users/${userId}/details`,
        GET_ACTIVITY_LOG: (userId: string, courseId: string) => `/api/users/${userId}/courses/${courseId}/activity-log`,
    },
    // User Bulk Import endpoints
    USER_BULK_IMPORT: {
        INITIATE: '/api/users-bulk-import/initiate',
        LIST: '/api/users-bulk-import',
        STATUS: (importId: string) => `/api/users-bulk-import/${importId}/status`,
        RECORDS: (importId: string) => `/api/users-bulk-import/${importId}/records`,
    },
    STORAGE: {
        UPLOAD: '/storage/utils/upload',
        DOWNLOAD: '/storage/utils/download',
    },
    // Groups endpoints - matches backend GroupController
    GROUPS: {
         DROPDOWN:'group-role-map/list',
        LIST: '/groups/list',
        GET_BY_KEY: (key: string) => `/groups/${key}`,
        CREATE: '/groups',
        UPDATE: (key: string) => `/groups/${key}`,
        DELETE: (key: string) => `/groups/${key}`,
        GET_ROLES: (key: string) => `/groups/${key}/roles`,
        // DROPDOWN: '/groups/utils/dropdown',
        // Lightweight server-side department search (name+key only, no analytics).
        // GET with ?search=&limit= — used by Global Search "Departments" tab.
        SEARCH: '/groups/utils/dropdown',
        ALL: '/groups/utils/all',
        TREE: '/groups/utils/tree',
    },
    // Roles endpoints - matches backend RoleController
    ROLES: {
        LIST: '/roles/list',
        DROPDOWN: '/roles/utils/dropdown',
        GET_BY_KEY: (key: string) => `/roles/${key}`,
        CREATE: '/roles',
        UPDATE: (key: string) => `/roles/${key}`,
        DELETE: (key: string) => `/roles/${key}`,
    },
    // Group Role Map endpoints
    GROUP_ROLE_MAP: {
        CREATE: '/group-role-map',
        GET_BY_KEY: (key: string) => `/group-role-map/${key}`,
        UPDATE: (key: string) => `/group-role-map/${key}`,
        DELETE: (key: string) => `/group-role-map/${key}`,
        UPSERT: '/group-role-map/upsert',
    },
    // SCIM endpoints - Direct sync from Skillzen Auth API
    SCIM: {
        CONFIG: '/scim/configurations',
        LIST: '/scim/configurations/list',
        FULL_SYNC: '/scim/sync',
        GROUPS_SYNC: '/scim/sync/groups',
        USERS_SYNC: '/scim/sync/users',
        TEST_CONNECTION: '/scim/test-connection',
        SYNC_LOGS: '/scim/sync-logs',
        LIST_SYNC_LOGS: '/scim/sync-logs/list',
        GET_SYNC_LOG: (id: string) => `/scim/sync-logs/${id}`,
        CLEANUP_SYNC_LOGS: '/scim/sync-logs/cleanup',
        STATISTICS: '/scim/statistics',
    },
    // LTI 1.3 admin endpoints - register/manage LMS platforms that launch Skillzen
    LTI: {
        PLATFORMS: '/lti/admin/platforms',
        PLATFORM_BY_ID: (id: string) => `/lti/admin/platforms/${id}`,
        PLATFORM_SUSPEND: (id: string) => `/lti/admin/platforms/${id}/suspend`,
        PLATFORM_ACTIVATE: (id: string) => `/lti/admin/platforms/${id}/activate`,
        CONFIG: '/lti/admin/config',
        RESOURCE_LINKS: '/lti/admin/resource-links',
        RESOURCE_LINK_BY_ID: (issuer: string, resourceLinkId: string) =>
            `/lti/admin/resource-links/${encodeURIComponent(issuer)}/${encodeURIComponent(resourceLinkId)}`,
        JWKS: '/lti/admin/jwks',
        // Deep Linking: the teacher's content selection, signed for return to the LMS
        DEEP_LINKING_COMPLETE: '/lti/deep-linking/complete',
        // Links launched from an LMS that nobody has mapped to Skillzen content yet
        UNMAPPED_LINKS: '/lti/admin/unmapped-links',
    },
    DASHBOARD: {
        // Dashboard Statistics
        STATS: '/api/dashboard/stats',
        DEPARTMENT_STATS: '/api/dashboard/list-groups',
        
        // Recent Activities
        RECENT_ACTIVITIES: '/api/dashboard/recent-activities',
        RECENT_ACTIVITIES_FILTER:'/api/dashboard/recent-activities-filter',
        LOG_ACTIVITY: '/api/dashboard/recent-activities/log',
        ACTIVITY_TRENDS: '/api/dashboard/recent-activities/trends',
        
        // Activity Metrics
        ACTIVITY_METRICS: '/api/dashboard/activity-metrics',

        // Assignment Status Stats
        ASSIGNMENT_STATUS_STATS: '/api/dashboard/assignment-status-stats',

        // Certificate Earned Stats
        CERTIFICATE_EARNED_STATS: '/api/dashboard/certificate-earned-stats',
        
        // Legacy endpoints (kept for backward compatibility)
        COURSE_STATUS: '/dashboard/course-status',
        ACTIVITY: '/dashboard/user-activity',
        CERTIFICATION_PROGRESS: '/dashboard/certification-progress',
        TOP_COURSES: '/dashboard/top-courses',
        CERTIFICATION_LEADERBOARD: '/dashboard/certification-leaderboard',
        REPORTS: '/dashboard/recent-activities',
        NOTIFICATIONS: '/dashboard/notifications',
    },
    
    // Course Assignment endpoints - matches backend CourseAssignmentController
    COURSE_ASSIGNMENTS: {
        // Core assignment operations
        ASSIGN_TO_USERS: '/api/course-assignments/assign/users',
        ASSIGN_TO_GROUPS: '/api/course-assignments/assign/groups',
        ASSIGN_TO_ROLES: '/api/course-assignments/assign/roles',
        CHECK_DUPLICATES: '/api/course-assignments/check-duplicates',
        REVOKE_ASSIGNMENT: (id: string) => `/api/course-assignments/revoke/assignment/${id}`,

        // Assignment management
        LIST: '/api/course-assignments/list',
        RECENT: '/api/course-assignments/recent',
        UPCOMING: '/api/course-assignments/upcoming',
        GET_BY_ID: (id: string) => `/api/course-assignments/${id}`,
        UPDATE: (id: string) => `/api/course-assignments/${id}`,
        DELETE: (id: string) => `/api/course-assignments/${id}`,

        // Notifications
        SEND_REMINDERS: (id: string) => `/api/course-assignments/${id}/send-reminders`,
        DEADLINE_NOTIFICATIONS: '/api/course-assignments/notifications/deadline-approaching',
        RETRY_FAILED_NOTIFICATIONS: '/api/course-assignments/notifications/retry-failed',

        // Statistics
        UPDATE_STATS: (id: string) => `/api/course-assignments/${id}/stats`,

        // Bulk Import
        BULK_TEMPLATE: '/api/course-assignments/bulk/template',
        BULK_IMPORT: '/api/course-assignments/bulk/import',
        BULK_VALIDATE: '/api/course-assignments/bulk/validate',
        BULK_IMPORT_HISTORY: '/api/course-assignments/bulk/bulk-assignment-history',
    },

    // Learning Path Assignment endpoints - matches backend LearningPathAssignmentController
    LEARNING_PATH_ASSIGNMENTS: {
        // Core assignment operations
        ASSIGN_TO_USERS: '/api/learning-path-assignments/assign/users',
        ASSIGN_TO_GROUPS: '/api/learning-path-assignments/assign/groups',
        ASSIGN_TO_ROLES: '/api/learning-path-assignments/assign/roles',
        CHECK_DUPLICATES: '/api/learning-path-assignments/check-duplicates',
        REVOKE_ASSIGNMENT: (id: string) => `/api/learning-path-assignments/revoke/assignment/${id}`,

        // Assignment management
        LIST: '/api/learning-path-assignments/list',
        RECENT: '/api/learning-path-assignments/recent',
        GET_BY_ID: (id: string) => `/api/learning-path-assignments/${id}`,
        UPDATE: (id: string) => `/api/learning-path-assignments/${id}`,
        DELETE: (id: string) => `/api/learning-path-assignments/${id}`,

        // Notifications
        SEND_REMINDERS: (id: string) => `/api/learning-path-assignments/${id}/send-reminders`,

        // Analytics
        OVERVIEW: '/api/learning-path-assignments/overview',

        // Statistics
        UPDATE_STATS: (id: string) => `/api/learning-path-assignments/${id}/stats`,

        // Bulk Import
        BULK_VALIDATE: '/api/learning-path-assignments/validate',
        BULK_IMPORT: '/api/learning-path-assignments/bulk-import',
        BULK_HISTORY: '/api/learning-path-assignments/bulk-learning-path-history',
    },

    // Course Tracking endpoints - matches backend CourseTrackingController
    COURSE_TRACKING: {
        // User progress
        USER_PROGRESS: (userId: string) => `/api/course-tracking/user/${userId}/progress`,
        UPDATE_PROGRESS: (trackingId: string) => `/api/course-tracking/${trackingId}/progress`,
        COMPLETE_COURSE: (trackingId: string) => `/api/course-tracking/${trackingId}/complete`,
        RESET_PROGRESS: (trackingId: string) => `/api/course-tracking/${trackingId}/reset`,
        
        // Unified tracking endpoint (SDK v6.0.291+)
        // Single endpoint for all tracking: course, module, content-item, and assessment tracking
        // Backend routes automatically based on provided IDs (course_id, module_id, lesson_id, assessment_id)
        UPDATE_LESSON_PROGRESS: '/api/course-tracking/lesson-progress',

        // Current user endpoints
        UPDATE_USER_PROGRESS: (userId: string) => `/api/course-tracking/user/${userId}/progress`,
        MY_DASHBOARD: '/api/course-tracking/dashboard',
        MY_COURSES: '/api/course-tracking/my-courses',
        MY_COURSE_DETAILS: (courseId: string) => `/api/course-tracking/my-courses/${courseId}`,
        GET_USER_PROGRESS: (userId: string) => `/api/course-tracking/user/${userId}`,
        USER_SUMMARY: (userId: string) => `/api/course-tracking/user/${userId}/summary`,
        USER_METRICS: (userId: string) => `/api/course-tracking/user/${userId}/metrics`,
        COMPLETE_COURSE_BY_ID: (courseId: string) => `/api/course-tracking/completion/${courseId}`,
        
        // Analytics and reporting
        ASSIGNMENT_SUMMARY: (assignmentId: string) => `/api/course-tracking/assignment/${assignmentId}/summary`,
        
        // Bulk operations
        BULK_UPDATE: '/api/course-tracking/bulk-update',
        EXPIRING_COURSES: '/api/course-tracking/expiring',
        UPDATE_SETTINGS: (trackingId: string) => `/api/course-tracking/${trackingId}/settings`,
        REVOKE: (courseId: string, userId: string) => `/api/course-tracking/revoke/course/${courseId}/user/${userId}`,
    },
    
    // Assignment Conflicts endpoints - matches backend AssignmentConflictsController
    ASSIGNMENT_CONFLICTS: {
        USER_REPORT: (userId: string) => `/api/assignment-conflicts/user/${userId}/report`,
        RESOLVE_USER_COURSE: (userId: string, courseId: string) => `/api/assignment-conflicts/user/${userId}/course/${courseId}/resolve`,
    },
    
    // Assignment Templates endpoints - matches backend AssignmentTemplatesController
    ASSIGNMENT_TEMPLATES: {
        // Template management
        CREATE: '/api/assignment-templates',
        LIST: '/api/assignment-templates',
        GET_BY_ID: (id: string) => `/api/assignment-templates/${id}`,
        GET_BY_KEY: (key: string) => `/api/assignment-templates/key/${key}`,
        UPDATE: (id: string) => `/api/assignment-templates/${id}`,
        DELETE: (id: string) => `/api/assignment-templates/${id}`,
        
        // Template operations
        POPULAR: '/api/assignment-templates/popular/list',
        APPLY: '/api/assignment-templates/apply',
        CLONE: (id: string) => `/api/assignment-templates/${id}/clone`,
    },

    // Authentication endpoints - matches backend AuthController
    AUTH: {
        LOGIN: '/api/auth/login',
        LOGOUT: '/api/auth/logout',
        REFRESH: '/api/auth/refresh',
        CLEAR_COOKIES: '/api/auth/clear-cookies',
        VALIDATE: '/api/auth/validate',
        ME: '/api/auth/me',
        TENANT_STATUS: '/api/auth/tenant-status',
        VERIFY_RESOURCE_ACCESS: '/api/auth/verify-resource-access',
        SSO_PROVIDERS: '/api/auth/sso/providers',
        SSO_LINKED_ACCOUNTS: '/api/auth/sso/linked-accounts',
        SSO_LINK: '/api/auth/sso/link',
        SSO_AUTHORIZE: (providerName: string) => `/api/auth/sso/${providerName}/authorize`,
        SSO_UNLINK: (providerId: string) => `/api/auth/sso/unlink/${providerId}`,
    },

    // Password management endpoints - matches backend PasswordController
    PASSWORD: {
        FORGOT: '/api/password/forgot',
        VALIDATE_TOKEN: '/api/password/validate-token',
        RESET: '/api/password/reset',
        CHANGE: '/api/password/change',
        CHECK_STRENGTH: '/api/password/check-strength',
    },

    // Registration endpoints - matches backend RegistrationController
    REGISTRATION: {
        VALIDATE: '/api/register/validate',
        SELF: '/api/register/self',
        INVITE: '/api/register/invite',
        VERIFY_EMAIL: '/api/register/verify-email',
        RESEND_VERIFICATION: '/api/register/resend-verification',
    },

    // Email Communication endpoints
    EMAIL_COMMUNICATION: {
        SEND: '/api/communications/email/send',
        GET_TEMPLATES: '/api/communications/email/templates',
    },

    // User Invitation endpoints - matches backend UserInvitationController
    USER_INVITATIONS: {
        // Core CRUD operations
        LIST: '/api/user-invitations/list',
        CREATE: '/api/user-invitations',
        GET_BY_ID: (id: string) => `/api/user-invitations/${id}`,
        UPDATE: (id: string) => `/api/user-invitations/${id}`,
        DELETE: (id: string) => `/api/user-invitations/${id}`,
        
        // Invitation operations
        RESEND: (id: string) => `/api/user-invitations/${id}/resend`,
        BULK_RESEND: '/api/user-invitations/bulk-resend',
        BY_STATUS: (status: string) => `/api/user-invitations/by-status/${status}`,
        BY_GROUP: (groupKey: string) => `/api/user-invitations/by-group/${groupKey}`,
        BULK_CANCEL:'/api/user-invitations/bulk-cancel',
        
        // Export & Metrics
        EXPORT_CSV: '/api/user-invitations/export/csv',
        METRICS: '/api/user-invitations/metrics/summary',
        
        // Email Template Management
        TEMPLATES: {
            LIST: '/api/user-invitations/templates',
            CREATE: '/api/user-invitations/templates',
            INITIALIZE: '/api/user-invitations/templates/initialize',
            GET_BY_ID: (id: string) => `/api/user-invitations/templates/${id}`,
            UPDATE: (id: string) => `/api/user-invitations/templates/${id}`,
            DELETE: (id: string) => `/api/user-invitations/templates/${id}`,
            CLONE: (id: string) => `/api/user-invitations/templates/${id}/clone`,
            VALIDATE: '/api/user-invitations/templates/validate',
        },
        TEST_EMAIL: '/api/user-invitations/test-email',
        
        // Public endpoints (no auth required)
        PUBLIC: {
            VERIFY: '/api/public/invitation/verify',
            ACCEPT: '/api/public/invitation/accept',
            REGISTER: '/api/public/invitation/register',
            STATUS: '/api/public/invitation/status',
            HEALTH: '/api/public/invitation/health',
        }
    },

    // Public Certificate Verification endpoints (no auth required)
    PUBLIC_CERTIFICATES: {
        VERIFY: (certificateNumber: string) => `/api/public/certificates/verify/${certificateNumber}`,
    },

    // Login Providers / SSO endpoints - matches backend LoginProviderController
    LOGIN_PROVIDERS: {
        LIST: '/api/login-providers',
        CREATE: '/api/login-providers',
        GET_BY_ID: (id: string) => `/api/login-providers/${id}`,
        UPDATE: (id: string) => `/api/login-providers/${id}`,
        DELETE: (id: string) => `/api/login-providers/${id}`,
        TEST: (id: string) => `/api/login-providers/${id}/test`,
        SYNC_WELLKNOWN: (id: string) => `/api/login-providers/${id}/sync-wellknown`,
    },

    LEADERBOARD:{
        LIST: 'api/leader-board/top-users',
        USER: 'api/leader-board/users-performance',
        MY_COURSE_METRICS: 'api/leader-board/my-metrics',
        MY_CERTIFICATION_METRICS: 'api/leader-board/my-metrics',
        ACKNOWLEDGE_BADGES: 'api/leader-board/badges/acknowledge',
        BADGES_METRICS: 'api/leader-board/badges/metrics',
        BADGES_LIST: 'api/leader-board/badges/list',
        BADGES_LOCKED: 'api/leader-board/badges/locked',
    },

    // Certifications endpoints
    CERTIFICATIONS: {
        LIST: '/certifications',
        MINIMAL_LIST: '/certification-exams/minimal-certification-list',
        RECENT_GENERATIONS: '/certifications/utils/list',
        CREATE: '/certifications',
        CREATE_MANUAL: '/certifications/manual',
        GET_BY_ID: (id: string) => `/certifications/${id}`,
        GET_STATUS: (id: string) => `/certifications/${id}/status`,
        RETRY_GENERATION: (id: string) => `/certifications/${id}/retry`,
        ENHANCE_INPUT: '/certifications/utils/enhance-input',
        VALIDATE_SELECTION: '/certifications/utils/validate-selection',
        GET_DETAILS: (certification_id: string) => `/certification-exams/certification/${certification_id}`,
        UPDATE: (id: string) => `/certifications/${id}`,
        DELETE: (id: string) => `/certifications/${id}`,
        ADD_QUESTION: (exam_id: string) => `/certification-exams/${exam_id}/questions`,
        UPDATE_QUESTIONS: (exam_id: string) => `/certification-exams/${exam_id}/questions`,
        REMOVE_QUESTION: (exam_id: string, question_id: string) => `/certification-exams/${exam_id}/questions/${question_id}`,
        PUBLISH: (exam_id: string) => `/certification-exams/${exam_id}/publish`,
        UPDATE_SETTINGS: (exam_id: string) => `/certification-exams/${exam_id}/settings`,
        MY_CERTIFICATIONS: '/api/certification-tracking/my-certification',
        DASHBOARD: '/api/certification-tracking/dashboard',
        START_EXAM: (certification_id: string) => `/learner/certifications/${certification_id}/start-exam`,
        LATEST_ATTEMPTS: (certification_id: string) => `/learner/certifications/${certification_id}/latest-attempts`,
        LIST_ATTEMPTS: (certification_id: string) => `/learner/certifications/${certification_id}/attempts`,
        RETRY_EXAM: (attempt_id: string, certification_id: string) => `/learner/certifications/retry/${attempt_id}/${certification_id}`,
        UPDATE_METRICS: (certification_id: string, attempt_id: string) => `/learner/certifications/${certification_id}/${attempt_id}/update-metrics`,
        SUBMIT_ANSWER: (attempt_id: string) => `/learner/certifications/exam-attempts/${attempt_id}/submit-answer`,
        SUBMIT_EXAM: (attempt_id: string) => `/learner/certifications/exam-attempts/${attempt_id}/submit`,
        ABANDON_ATTEMPT: (attempt_id: string) => `/learner/certifications/exam-attempts/${attempt_id}/abandon`,
        DELETE_ANSWER: (attempt_id: string, question_id: string) => `/learner/certifications/exam-attempts/${attempt_id}/questions/${question_id}/answer`,
    },

    // Category endpoints - matches backend CategoryController
    CATEGORIES: {
        // Core CRUD operations
        CREATE: '/category',
        GET_BY_ID: (id: string) => `/category/${id}`,
        UPDATE: (id: string) => `/category/${id}`,
        DELETE: (id: string) => `/category/${id}`,
        
        // Tree and hierarchy
        TREE: '/category/tree',
        PARENT_CATEGORIES: '/category/parent-category',
        
        // List operations
        LIST: '/category/utils/list',
        DROPDOWN: '/category/list',
        PARENT_DROPDOWN: '/category/list/parent-category',
        
        // Legacy support (keeping for backward compatibility)
        LIST_PARENT: '/category/list/parent-category',
    },

    // Business Settings endpoints - matches backend BusinessSettingsController
    BUSINESS_SETTINGS: {
        GET: '/business-settings',
        UPDATE: '/business-settings',
    },

    // AI Model Settings endpoints - matches backend AIModelSettingsController
    AI_MODEL_SETTINGS: {
        CATALOG: '/ai-model-settings/catalog',
        GET: '/ai-model-settings',
        UPDATE: '/ai-model-settings',
    },

    // Credit Plan endpoints (management-only) - matches backend CreditPlanController
    CREDIT_PLAN: {
        LIST: '/credit-plan/list',   // POST — optional { search } in the body
        CREATE: '/credit-plan',
        GET: '/credit-plan/get',
        MY_SUMMARY: '/credit-plan/my-summary',  // POST — caller tenant's own live credit summary (not management-gated)
        UPDATE: (planKey: string) => `/credit-plan/${planKey}`,
        DELETE: (planKey: string) => `/credit-plan/${planKey}`,
    },

    // Credit Rate endpoints (management-only, singleton) - matches backend CreditRateController
    CREDIT_RATE: {
        GET: '/credit-rate',
        SAVE: '/credit-rate',
    },

    // Tenant ↔ Credit Plan mapping (management-only) - matches backend TenantCreditPlanController
    TENANT_CREDIT_PLAN: {
        LIST: '/tenant-credit-plan/list',   // POST — optional { search } in the body
        ASSIGN: '/tenant-credit-plan/assign',
        UNASSIGN: '/tenant-credit-plan/unassign',
    },

    // Calendar Events endpoints - matches backend CalendarController
    CALENDAR_EVENTS: {
        // Main unified calendar endpoint (GET /calendar?period=day|week|month)
        LIST: '/calendar',
        // Calendar statistics (GET /calendar/stats?period=day|week|month)
        STATS: '/calendar/stats',
    },

    // Live Sessions endpoints - matches backend LiveSessionsController
    LIVE_SESSIONS: {
        // CRUD operations
        CREATE: '/live-sessions',
        LIST: '/live-sessions',
        GET_BY_ID: (id: string) => `/live-sessions/${id}`,
        UPDATE: (id: string) => `/live-sessions/${id}`,
        DELETE: (id: string) => `/live-sessions/${id}`,
        
        // Registration operations
        REGISTER: (id: string) => `/live-sessions/${id}/register`,
        UNREGISTER: (sessionId: string, userId: string) => `/live-sessions/${sessionId}/register/${userId}`,
        
        // User-specific operations
        USER_SESSIONS: (userId: string) => `/live-sessions/user/${userId}`,
        TODAY_SCHEDULE: '/live-sessions/schedule/today',
    },

    // Cache Storage Redis endpoints - matches backend CacheStorageRedisController
    CACHE_STORAGE: {
        COLLECTION_TIMESTAMPS: '/cache-storage/get-last-updated-collection-fields',
        KEYS: '/cache-storage/keys',
        STATS: '/cache-storage/stats',
    },

    // Learning Streak endpoints - matches backend LearningStreakController
    LEARNING_STREAK: {
        CURRENT: '/api/learner/streak/current',
        LEADERBOARD: '/api/learner/streak/leaderboard',
        FREEZE: '/api/learner/streak/freeze',
        UPDATE_ACTIVITY: '/api/learner/streak/activity',
        GET_USER_STREAK: (userId: string) => `/api/learner/streak/user/${userId}`,
        ADMIN_PROCESS_DAILY: '/api/learner/streak/admin/process-daily-updates',
    },
 
    GAMIFICATION: {
    BADGES: {
      CREATE: '/gamification/badges',
      LIST: '/gamification/badges/list',

      METRICS: '/gamification/badges/metrics/overview',

      GET_BY_ID: (id: string) => `/gamification/badges/${id}`,

      UPDATE: (id: string) => `/gamification/badges/${id}`,

      DELETE: (id: string) => `/gamification/badges/${id}`,
      TOGGLE_ACTIVE: (id: string) => `/gamification/badges/${id}/toggle-active`,
    },
    },

    // User Achievements endpoints - matches backend UserAchievementsController
    USER_ACHIEVEMENTS: {
        LIST: '/api/user-achievements/list',
        METRICS: '/api/user-achievements/metrics',
        DETAILS: '/api/user-achievements/details',
    },

    // Document Store endpoints - matches backend DocumentController
    DOCUMENT_STORE: {
        // Core CRUD operations
        LIST: '/document-store/list',  // POST endpoint for pagination
        METRICS: '/document-store/metrics',  // GET lightweight metrics
        CREATE: '/document-store',
        GET_BY_ID: (id: string) => `/document-store/${id}`,
        UPDATE: (id: string) => `/document-store/${id}`,
        DELETE: (id: string) => `/document-store/${id}`,

        // Utility endpoints
        DROPDOWN_LIST: '/document-store/dropdown-list',
        COURSE_STRUCTURE_ANALYSIS: (id: string) => `/document-store/${id}/course-structure-analysis`,
        SEARCH: '/document-store/utils/search',
        ENHANCE_DESCRIPTION: '/document-store/utils/enhance-description',
    },

    // Webhook Configuration endpoints - matches backend WebhookConfigController
    WEBHOOKS: {
        CREATE: '/webhook-config',
        LIST: '/webhook-config/utils/list',
        TRIGGER_TYPES: '/webhook-config/utils/trigger-type-dropdown',
        GET_BY_ID: (id: string) => `/webhook-config/${id}`,
        UPDATE: (id: string) => `/webhook-config/${id}`,
        DELETE: (id: string) => `/webhook-config/${id}`,
        TEST: (id: string) => `/webhook-config/${id}/test`,
        GET_HISTORY: (id: string) => `/webhook-config/${id}/history`,
    },

    // Trending Topics endpoints - matches backend TrendingTopicController
    TRENDING_TOPICS: {
        LIST: '/trending-topic-identification/list',
        DISCOVER: '/trending-topic-identification/discover',
    },

    // Course endpoints - matches backend CourseController
    COURSES: {
        CREATE: '/course/create',
        GENERATE: '/course/generate',
        LIST: '/course/list',
        LIST_MINIMAL: '/course/list-minimal',
        GET_BY_ID: (id: string) => `/course/${id}`,
        // Optional language to read in, e.g. 'hi-IN'. The id stays the MASTER
        // course id in every language.
        // translation is a view of one course, not a second course, so
        // enrolment and progress keep pointing at a single id.
        GET_DETAILS: (id: string, language_code?: string) =>
            `/course/details/${id}${language_code ? `?language=${encodeURIComponent(language_code)}` : ''}`,
        GET_DETAILS_BY_SLUG: (slug: string) => `/course/details/slug/${slug}`,
        // The learner player's single read. language_code selects the language
        // version; courseId stays the MASTER's in every language — see
        // GET_DETAILS above.
        GET_COMPLETE: (courseId: string, language_code?: string) =>
            `/course/${courseId}/complete${language_code ? `?language=${encodeURIComponent(language_code)}` : ''}`,
        UPDATE: (id: string) => `/course/${id}`,
        UPDATE_METADATA: (courseId: string) => `/course/metadata/${courseId}`,
        UPDATE_STAGE: (id: string) => `/course/${id}/stage`,
        // User Course Progress specific endpoints
        GET_COURSE_DETAILS_PROGRESS: (courseId: string) => `/api/courses/${courseId}/details`,
        GET_MODULES_DETAILED: (courseId: string) => `/api/courses/${courseId}/modules/detailed`,
        DELETE: (id: string) => `/course/${id}`,
        DUPLICATE: (id: string) => `/course/${id}/duplicate`,
        GET_MODULES: (courseId: string) => `/course/${courseId}/modules`,
        // language_code serves that language's lesson content. moduleId stays the
        // MASTER module id in every language — see GET_DETAILS above.
        MODULE_CONTENT_TREE: (moduleId: string, language_code?: string) =>
            `/course/module/${moduleId}/content-tree${language_code ? `?language=${encodeURIComponent(language_code)}` : ''}`,
        // Manual course creation (empty skeleton, no AI generation)
        CREATE_MANUAL: '/course/manual',
        // Manual module editing (add / update / delete / reorder)
        CREATE_MODULE: (courseId: string) => `/course/${courseId}/module`,
        UPDATE_MODULE: (moduleId: string) => `/course/module/${moduleId}`,
        DELETE_MODULE: (moduleId: string) => `/course/module/${moduleId}`,
        REORDER_MODULES: (courseId: string) => `/course/${courseId}/modules/reorder`,
        // Manual content-item editing (add / delete / reorder; edit uses UPDATE_CONTENT_ITEM)
        CREATE_CONTENT_ITEM: (moduleId: string) => `/course/module/${moduleId}/content-item`,
        DELETE_CONTENT_ITEM: (contentItemId: string) => `/course/content-item/${contentItemId}`,
        REORDER_CONTENT_ITEMS: (moduleId: string) => `/course/module/${moduleId}/content-items/reorder`,
        CONTENT_ITEM_SCENES: (contentItemId: string) => `/course/content-item/${contentItemId}/scenes`,
        GET_FULL_COURSE: (courseId: string) => `/course/full/${courseId}`,
        GET_TRACKING: (courseId: string) => `/course/${courseId}/tracking`,
        // Assessment endpoints
        GET_ASSESSMENT_ADMIN: (assessmentId: string) => `/course/assessment/${assessmentId}/admin`,
        GET_ASSESSMENT_LEARNER: (assessmentId: string) => `/course/assessment/${assessmentId}/learner`,
        GET_ALL_ACTIVE_COURSES:'api/assignment-analytics/courses',
        ENHANCE_INPUT: '/course/utils/enhance-input',
        // Content editing endpoints
        UPDATE_CONTENT_ITEM: (id: string) => `/course/content-item/${id}`,
        UPDATE_SCENE: (id: string) => `/course/scene/${id}`,
        // Manual scene editing (add / delete / reorder; edit uses UPDATE_SCENE)
        CREATE_SCENE: (contentItemId: string) => `/course/content-item/${contentItemId}/scene`,
        DELETE_SCENE: (sceneId: string) => `/course/scene/${sceneId}`,
        REORDER_SCENES: (contentItemId: string) => `/course/content-item/${contentItemId}/scenes/reorder`,
        SCENE_STATUS: (id: string) => `/course/scene/${id}/status`,
        REGENERATE_SCENE_VIDEO: (id: string) => `/course/scene/${id}/regenerate-video`,
        // AI regenerate/generate a scene's HTML slide content
        REGENERATE_SCENE_HTML: (id: string) => `/course/scene/${id}/regenerate-html`,
        // Poll AI HTML regeneration status for a content item (fire-and-forget)
        CONTENT_ITEM_HTML_STATUS: (id: string) => `/course/content-item/${id}/html-status`,
        // Upload a PDF file into an empty html/html_with_audio content item
        UPLOAD_CONTENT_DOCUMENT: (id: string) => `/course/content-item/${id}/upload-document`,
        // Remove the uploaded file (reset the item to empty, keep the item)
        REMOVE_CONTENT_DOCUMENT: (id: string) => `/course/content-item/${id}/remove-document`,
        // Upload an MP4 into a scene-less video_presentation content item
        UPLOAD_CONTENT_VIDEO: (id: string) => `/course/content-item/${id}/upload-video`,
        // Remove the uploaded video (keep the item, restore scene tools)
        REMOVE_CONTENT_VIDEO: (id: string) => `/course/content-item/${id}/remove-video`,
        GENERATE_NARRATION: (id: string) => `/course/content-item/${id}/generate-narration`,
        // Generate a scene's narration from its slide HTML (capped at 1500 chars)
        GENERATE_SCENE_NARRATION: (id: string) => `/course/scene/${id}/generate-narration`,
        REGENERATE_CONTENT: (id: string) => `/course/content-item/${id}/regenerate-content`,
        REGENERATE_AUDIO: (id: string) => `/course/content-item/${id}/regenerate-audio`,
        REGENERATE_VIDEO: (id: string) => `/course/content-item/${id}/regenerate-video`,
        CONTENT_ITEM_VIDEO_STATUS: (id: string) => `/course/content-item/${id}/video-status`,
    },

    // Course Orchestration endpoints - matches backend CourseOrchestrationController
    COURSE_ORCHESTRATION: {
        BASE: '/course-orchestration',
        LIST: '/course-orchestration/utils/list',
        CREATE_INITIATE: '/course-orchestration/utils/create-course/initiate',
        GET_BY_ID: (id: string) => `/course-orchestration/${id}`,
        GET_STATUS: (id: string) => `/course-orchestration/utils/status/${id}`,
        UPDATE: (id: string) => `/course-orchestration/${id}`,
        DELETE: (id: string) => `/course-orchestration/${id}`,
        // Retry operations
        RETRY: (id: string) => `/course-orchestration/${id}/retry`,
        RETRY_SCENES: (id: string) => `/course-orchestration/${id}/retry-scenes`,
        RETRY_SCENES_BATCH: (id: string) => `/course-orchestration/${id}/retry-scenes`,
        STAGE_STATUS: (id: string) => `/course-orchestration/${id}/stage-status`,
        BATCH_RETRY: '/course-orchestration/utils/batch-retry',
        // Recovery operations
        RECOVER_SINGLE: (id: string) => `/course-orchestration/utils/recover-single/${id}`,
        RECOVER_ALL_STUCK: '/course-orchestration/utils/recover-stuck',
        RECOVERY_STATUS: '/course-orchestration/utils/recovery-status',
        REGENERATE_FULL_COURSE: (id: string) => `/course-orchestration/${id}/regenerate-full-course`,
        // Synchronous generation
        SYNC_GENERATE: '/course-orchestration/sync/generate',
        SYNC_RESUME: '/course-orchestration/sync/resume',
        SYNC_STATUS: (id: string) => `/course-orchestration/sync/status/${id}`,
        // Language configuration endpoints
        LANGUAGES: {
            SUPPORTED: '/course-orchestration/languages/supported',
            INDIAN: '/course-orchestration/languages/indian',
            CONFIGURATION: (languageCode: string) => `/course-orchestration/languages/configuration/${languageCode}`,
            VALIDATE: '/course-orchestration/languages/validate',
            FORMAT_SSML: '/course-orchestration/languages/format-ssml',
            GENERATE_CAPTIONS: '/course-orchestration/languages/generate-captions',
            COURSE_WITH_LANGUAGE: '/course-orchestration/languages/course-with-language',
        },
    },

    // Permission endpoints - matches backend PermissionController
    PERMISSIONS: {
        LIST: '/permissions/list',
        DROPDOWN: '/permissions/utils/dropdown',
        CREATE: '/permissions',
        GET_BY_KEY: (key: string) => `/permissions/${key}`,
        UPDATE: (key: string) => `/permissions/${key}`,
        DELETE: (key: string) => `/permissions/${key}`,
        ASSIGN_TO_ROLE: '/permissions/role/assign',
        GET_ROLE_PERMISSIONS: (role_key: string) => `/permissions/role/${role_key}`,
    },

    // Settings endpoints - matches backend SettingsController
    SETTINGS: {
        LIST_PROVIDERS: '/login-provider-srv/utils/list-all-providers',
        UPDATE_PROVIDERS: '/login-provider-srv/utils/list-all-providers',
    },

    // Text Enhancement endpoints - matches backend TextEnhancementController
    TEXT_ENHANCEMENT: {
        BASE: '/api/ai/text-enhancement',
        ENHANCE: '/api/ai/text-enhancement/enhance',
        HISTORY: '/api/ai/text-enhancement/history',
        GET_BY_ID: (id: string) => `/api/ai/text-enhancement/${id}`,
        REGENERATE: (id: string) => `/api/ai/text-enhancement/${id}/regenerate`,
        FEEDBACK: (id: string) => `/api/ai/text-enhancement/${id}/feedback`,
    },

    // Learning Assistant endpoints - matches backend LearningAssistantController
    LEARNING_ASSISTANT: {
        TEXT_CHAT: '/api/learning-assistant/chat/text',
        AUDIO_CHAT: '/api/learning-assistant/chat/audio',
        CHAT_HISTORY: (session_id: string) => `/api/learning-assistant/chat/history/${session_id}`,
        UPDATE_CONTEXT: '/api/learning-assistant/chat/context',
        END_SESSION: (session_id: string) => `/api/learning-assistant/chat/session/${session_id}`,
        SET_LANGUAGE: '/api/learning-assistant/chat/language',
        GET_LANGUAGES: '/api/learning-assistant/chat/languages',
        GET_GOOGLE_API_KEY: '/api/learning-assistant/google-api-key',
        GET_EPHEMERAL_TOKEN: '/api/learning-assistant/ephemeral-token',
    },

    // Live Avatar endpoints - matches backend LiveAvatarController
    LIVE_AVATAR: {
        SESSIONS: '/api/live-avatar/sessions',
        GET_SESSION: (id: string) => `/api/live-avatar/sessions/${id}`,
        DELETE_SESSION: (id: string) => `/api/live-avatar/sessions/${id}`,
        SUBMIT_ANSWER: '/api/live-avatar/sessions/answer',
        SUBMIT_ICE_CANDIDATE: '/api/live-avatar/sessions/ice-candidate',
        UPDATE_CONNECTION_STATE: '/api/live-avatar/sessions/connection-state',
        SEND_TALK: '/api/live-avatar/sessions/talk',
        INTERACTION_MODE: '/api/live-avatar/interaction-mode',
        GET_EPHEMERAL_TOKEN: '/api/live-avatar/ephemeral-token',
        GET_AVAILABLE_AVATARS: '/api/live-avatar/avatars',
        GET_AVAILABLE_VOICES: '/api/live-avatar/voices',
    },

    // Avatar Chat endpoints - D-ID Integration
    AVATAR_CHAT: {
        CREATE_SESSION: '/api/avatar-chat/session',
        SUBMIT_SDP: (sessionId: string) => `/api/avatar-chat/session/${sessionId}/sdp`,
        SUBMIT_ICE: (sessionId: string) => `/api/avatar-chat/session/${sessionId}/ice`,
        SEND_TALK: (sessionId: string) => `/api/avatar-chat/session/${sessionId}/talk`,
        SEND_CHAT: (sessionId: string) => `/api/avatar-chat/session/${sessionId}/chat`,
        UPDATE_STATE: (sessionId: string) => `/api/avatar-chat/session/${sessionId}/connection-state`,
        END_SESSION: (sessionId: string) => `/api/avatar-chat/session/${sessionId}`,
        GET_SESSION: (sessionId: string) => `/api/avatar-chat/session/${sessionId}`,
        GET_STATS: '/api/avatar-chat/sessions/stats',
        GET_AVATARS: '/api/avatar-chat/avatars',
        GET_VOICES: '/api/avatar-chat/voices',
    },

    // Analytics endpoints - matches backend AnalyticsController
    ANALYTICS: {
        OVERVIEW: '/analytics/overview',
        DEPARTMENT_PERFORMANCE: '/analytics/department-performance',
        LEARNING_TRENDS: '/analytics/learning-trends',
        SKILL_GAPS: '/analytics/skill-gaps',
        TOP_PERFORMERS: '/analytics/top-performers',
        TRAINING_DEADLINES: '/analytics/training-deadlines',
        TRAINING_STATUS:'/analytics/training-status',
        SEND_DEADLINE_REMINDER: '/analytics/send-deadline-reminder',
        // Organizational hierarchy dropdowns
        BU_HEAD_DROPDOWN: '/api/assignment-analytics/bu-head-dropdown',
        RM_DROPDOWN: '/api/assignment-analytics/rm-dropdown',
        HRBP_DROPDOWN: '/api/assignment-analytics/hrbp-dropdown',
    },

    // Overdue Report endpoints - matches backend OverdueReportController
    OVERDUE_REPORTS: {
        GENERATE: '/overdue-reports/generate',
        SUMMARY: '/overdue-reports/summary',
        EXPORT: '/overdue-reports/export',
    },

    // Full Learner Activity Report — same controller, broader scope (all trackings, not just overdue)
    LEARNER_ACTIVITY_REPORT: {
        GENERATE: '/overdue-reports/learner-activity-report/generate',
        SUMMARY: '/overdue-reports/learner-activity-report/summary',
        EXPORT: '/overdue-reports/learner-activity-report/export',
    },

    // Course Performance endpoints - matches backend CoursePerformanceController
    COURSE_PERFORMANCE: {
        METRICS: '/api/dashboard/course-performance/metrics',
        LIST: '/api/dashboard/course-performance/list',
        GET_BY_ID: (courseId: string) => `/api/dashboard/course-performance/${courseId}`,
        GET_USER_BASED_ON_COURSE:(courseId: string) => `/api/dashboard/course-performance/data/${courseId}`,
        GET_ANALYTICS: (courseId: string) => `/api/dashboard/course-performance/analytics/${courseId}`
    },

    // Certification Performance endpoints - matches backend CertificationPerformanceController
    CERTIFICATION_PERFORMANCE: {
        METRICS: '/dashboard/certifications/metrics',
        LIST: '/dashboard/certifications/performance',
        GET_BY_ID: (certificationId: string) => `/dashboard/certifications/performance/${certificationId}`,
        GET_USER_BASED_ON_CERTIFICATION: (certificationId: string) => `/dashboard/certifications/data/${certificationId}`,
        GET_ANALYTICS: (certificationId: string) => `/dashboard/certifications/analytics/${certificationId}`
    },

    // Learning Path Performance endpoints - matches backend LearningPathPerformanceController
    LEARNING_PATH_PERFORMANCE: {
        METRICS: '/learner/learning-paths/metrics',
        LIST: '/learner/learning-paths/list',
        GET_BY_ID: (learningPathId: string) => `/learner/learning-paths/courses/${learningPathId}`,
        GET_USER_BASED_ON_LEARNING_PATH: (learningPathId: string) => `/learner/learning-paths/${learningPathId}`,
        GET_USER_COURSES: (learningPathId: string, userId: string) => `/learner/learning-paths/${learningPathId}/users/${userId}/courses`,
        GET_USERS_PROGRESS: (learningPathId: string) => `/learner/learning-paths/${learningPathId}/users/progress`,
        GET_USERS_ANALYTICS: (learningPathId: string) => `/learner/learning-paths/${learningPathId}/users/analytics`,
        GET_USER_LEARNING_PATHS: (userId: string) => `/learner/learning-paths?user_id=${userId}`,
        GET_FULL_DETAILS: (learningPathId: string, userId: string) => `/learner/learning-paths/${learningPathId}/full-details?user_id=${userId}`,
        UPDATE_SETTINGS: (trackingId: string) => `/learner/learning-paths/${trackingId}/settings`,
        REVOKE: (learningPathId: string, userId: string) => `/learner/learning-paths/revoke/learning-path/${learningPathId}/user/${userId}`,
    },

    // Certification Tracking endpoints - matches backend CertificationTrackingController
    CERTIFICATION_TRACKING: {
        // POST - Get user certification progress with filters, sorting, pagination
        USER_PROGRESS: (userId: string) => `/api/certification-tracking/users/${userId}/progress`,
        // GET - Get user certification metrics
        USER_METRICS: (userId: string) => `/api/certification-tracking/users/${userId}/metrics`,
        UPDATE_SETTINGS: (trackingId: string) => `/api/certification-tracking/${trackingId}/settings`,
        REVOKE: (certificationId: string, userId: string) => `/api/certification-tracking/revoke/certification/${certificationId}/user/${userId}`,
    },

    // Assessment endpoints - matches backend AssessmentController
    ASSESSMENTS: {
        // Admin view - includes correct answers
        GET_ADMIN: (assessmentId: string) => `/course/assessment/${assessmentId}/admin`,

        // Learner view - sanitized (no correct answers)
        // language_code serves the question WORDING in that language. The
        // assessmentId stays the MASTER's, and ids/correct answers are shared
        // across languages, so grading and attempt limits are unaffected.
        GET_LEARNER: (assessmentId: string, language_code?: string) =>
            `/course/assessment/${assessmentId}/learner${language_code ? `?language=${encodeURIComponent(language_code)}` : ''}`,

        // Assessment Submission & Scoring (v6.0.211)
        // language_code is RECORDED on the attempt, so the language a learner
        // sat the paper in is a fact about the attempt rather than an editable
        // parameter. It never changes grading, which always uses the master.
        START_ATTEMPT: (assessmentId: string, language_code?: string) =>
            `/course/assessment/${assessmentId}/start${language_code ? `?language=${encodeURIComponent(language_code)}` : ''}`,
        SUBMIT_ATTEMPT: (attemptId: string) => `/course/assessment/attempt/${attemptId}/submit`,
        GET_ATTEMPT_HISTORY: (assessmentId: string) => `/course/assessment/${assessmentId}/attempts`,
        GET_ATTEMPT_DETAILS: (attemptId: string) => `/course/assessment/attempt/${attemptId}`,
        GET_USER_SUMMARY: (assessmentId: string) => `/course/assessment/${assessmentId}/summary`,

        // Every assessment on a course (Admin — Completion Config)
        LIST_FOR_COURSE: (courseId: string) => `/course/assessment/course/${courseId}`,
        // Mark whether passing this assessment is required to complete the course (Admin)
        SET_COMPLETION_REQUIREMENT: (assessmentId: string) =>
            `/course/assessment/${assessmentId}/completion-requirement`,
        // What the current learner must still pass to complete the course
        COMPLETION_REQUIREMENTS: (courseId: string) =>
            `/course/assessment/course/${courseId}/completion-requirements`,

        // Create a manual PRE/POST assessment for a course/module/content-item (Admin)
        CREATE: '/course/assessment/create',
        // Soft-delete a whole assessment (Admin)
        DELETE: (assessmentId: string) => `/course/assessment/${assessmentId}`,
        // Update questions (Admin)
        UPDATE_QUESTIONS: (assessmentId: string) => `/course/assessment/${assessmentId}/questions`,
        // Update assessment settings (Admin)
        UPDATE_SETTINGS: (assessmentId: string) => `/course/assessment/${assessmentId}/passing-score`,
        // Add question (Admin)
        ADD_QUESTION: (assessmentId: string) => `/course/assessment/${assessmentId}/questions`,
        // Delete question (Admin)
        DELETE_QUESTION: (assessmentId: string, questionId: string) => `/course/assessment/${assessmentId}/questions/${questionId}`,

        // Analytics (future endpoints)
        GET_ANALYTICS: (assessmentId: string) => `/course/assessment/${assessmentId}/analytics`,
    },

    // Reporting V2 endpoints - matches backend Reporting V2 API
    REPORTING_V2: {
        // Dashboards - Real-time analytics with caching (GET endpoints)
        DASHBOARDS: {
            ADMIN_OVERVIEW: '/reporting/v2/dashboards/admin/overview',
            EXECUTIVE_SUMMARY: '/reporting/v2/dashboards/admin/executive',
            MANAGER_TEAM: '/reporting/v2/dashboards/manager/team',
            LEARNER_PROGRESS: '/reporting/v2/dashboards/learner/progress',
            DEPARTMENT_PERFORMANCE: (departmentId: string) => `/reporting/v2/dashboards/admin/department/${departmentId}`,
            COURSE_ANALYTICS: (courseId: string) => `/reporting/v2/dashboards/admin/course/${courseId}`,
            REFRESH_ALL: '/reporting/v2/dashboards/admin/refresh-all',
            CACHE_STATS: '/reporting/v2/dashboards/admin/cache-stats',
        },

        // Reports - Historical snapshots with exports
        REPORTS: {
            // Generate admin reports (POST) - Only implemented endpoints
            GENERATE_ORGANIZATION_OVERVIEW: '/reporting/v2/reports/organization-overview',
            GENERATE_DEPARTMENT_PERFORMANCE: '/reporting/v2/reports/department-performance',
            GENERATE_ROLE_ANALYSIS: '/reporting/v2/reports/role-analysis',
            GENERATE_COURSE_EFFECTIVENESS: '/reporting/v2/reports/course-effectiveness',

            // Utility endpoints
            PREVIEW: (reportType: string) => `/reporting/v2/reports/${reportType}/preview`,
            EXPORT: (snapshotId: string, format: string) => `/reporting/v2/reports/${snapshotId}/export?format=${format}`,
            AUDIT_TRAIL: '/reporting/v2/reports/audit-trail',

            // Report management
            LIST: '/reporting/v2/reports',
            GET_BY_ID: (snapshotId: string) => `/reporting/v2/reports/${snapshotId}`,
            DELETE: (snapshotId: string) => `/reporting/v2/reports/${snapshotId}/delete`,

            // Legacy export endpoints (deprecated - use EXPORT instead)
            EXPORT_PDF: (snapshotId: string) => `/reporting/v2/reports/${snapshotId}/export?format=pdf`,
            EXPORT_EXCEL: (snapshotId: string) => `/reporting/v2/reports/${snapshotId}/export?format=excel`,
        },

        // Learner Reports - Individual learner snapshots (POST to generate)
        LEARNER_REPORTS: {
            // Generate learner reports (POST) - Only implemented endpoints
            PROGRESS: '/reporting/v2/reports/learner/progress',
            CERTIFICATIONS: '/reporting/v2/reports/learner/certifications',

            // Retrieve learner reports (GET)
            MY_REPORTS: '/reporting/v2/reports/learner/my-reports',
            GET_BY_ID: (reportId: string) => `/reporting/v2/reports/learner/${reportId}`,
        },

        // Schedules - Automated report generation
        SCHEDULES: {
            // CRUD operations
            LIST: '/reporting/v2/schedules',
            CREATE: '/reporting/v2/schedules',
            GET_BY_ID: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}`,
            UPDATE: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}`,
            DELETE: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}`,

            // Schedule operations
            EXECUTE: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}/execute`,
            PAUSE: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}/pause`,
            RESUME: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}/resume`,

            // Execution history
            EXECUTIONS: (scheduleId: string) => `/reporting/v2/schedules/${scheduleId}/executions`,
        },

        // Subscriptions - Automated email delivery
        SUBSCRIPTIONS: {
            // CRUD operations
            LIST: '/reporting/v2/subscriptions',
            CREATE: '/reporting/v2/subscriptions',
            GET_BY_ID: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}`,
            UPDATE: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}`,
            DELETE: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}`,

            // Subscription operations
            SEND_NOW: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}/send-now`,
            PAUSE: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}/pause`,
            RESUME: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}/resume`,

            // Delivery history
            DELIVERIES: (subscriptionId: string) => `/reporting/v2/subscriptions/${subscriptionId}/deliveries`,
        },
    },

    // Custom course completion config — matches backend CompletionConfigController
    COMPLETION_CONFIG: {
        // Admin: read the whole config (custom_enabled + conditions)
        GET: (courseId: string) => `/course/completion-config/${courseId}`,
        // Admin: toggle the master custom completion switch
        SET_CUSTOM_ENABLED: (courseId: string) => `/course/completion-config/${courseId}/custom-enabled`,
        // Admin: toggle the learner-side manual lesson completion control
        SET_MANUAL_COMPLETION_ENABLED: (courseId: string) =>
            `/course/completion-config/${courseId}/manual-completion-enabled`,
        // Admin: create/update the T&C condition inline (title / html / enabled)
        UPSERT_TERMS: (courseId: string) => `/course/completion-config/${courseId}/terms`,
        // Admin: upload a T&C PDF (multipart)
        UPLOAD_TERMS: (courseId: string) => `/course/completion-config/${courseId}/terms/upload`,
        // Admin: enable/disable a condition
        SET_CONDITION_ENABLED: (courseId: string, conditionId: string) =>
            `/course/completion-config/${courseId}/conditions/${conditionId}/enabled`,
        // Admin: remove a condition
        DELETE_CONDITION: (courseId: string, conditionId: string) =>
            `/course/completion-config/${courseId}/conditions/${conditionId}`,
        // Learner: aggregated completion gate (assessments + conditions)
        GATE: (courseId: string) => `/course/completion-config/${courseId}/gate`,
        // Learner: accept a T&C condition
        ACCEPT_TERMS: (courseId: string) => `/course/completion-config/${courseId}/terms/accept`,
        // Admin: a learner's T&C acceptance records (progress view)
        USER_ACCEPTANCES: (courseId: string, userId: string) =>
            `/course/completion-config/${courseId}/acceptances/${userId}`,
    },

    // Forum endpoints - matches backend ForumController
    FORUM: {
        // Thread CRUD operations
        CREATE_THREAD: '/forum/threads',
        LIST_THREADS: '/forum/threads/list',
        GET_THREAD: (threadId: string) => `/forum/threads/${threadId}`,
        UPDATE_THREAD: (threadId: string) => `/forum/threads/${threadId}`,
        DELETE_THREAD: (threadId: string) => `/forum/threads/${threadId}`,

        // Discussion (Comments) operations
        CREATE_DISCUSSION: '/forum/discussions',
        LIST_DISCUSSIONS: '/forum/discussions/list',
        UPDATE_DISCUSSION: '/forum/discussions',
        DELETE_DISCUSSION: '/forum/discussions',
        REACT_DISCUSSION: '/forum/discussions/react',
    },

    // Tenant endpoints - matches backend TenantsController
    TENANTS: {
        LIST: '/tenants',
        METRICS: '/tenants/metrics',
        MANAGEMENT_INFO: '/tenants/management-info',
        CREATE: '/tenants',
        GET_BY_KEY: (tenant_key: string) => `/tenants/${tenant_key}`,
        UPDATE: (tenant_key: string) => `/tenants/${tenant_key}`,
        UPDATE_STATUS: (tenant_key: string) => `/tenants/${tenant_key}/status`,
        DELETE: (tenant_key: string) => `/tenants/${tenant_key}`,
        SELF_SIGNUP: '/tenants/self-signup',
        CHECK_AVAILABILITY: (tenant_key: string) => `/tenants/check-availability/${tenant_key}`,
        CHECK_NAME_AVAILABILITY: (tenant_name: string) => `/tenants/check-name-availability/${encodeURIComponent(tenant_name)}`,
        // GitLab Ingress Integration
        VIEW_INGRESS_YAML: '/tenants/ingress/view',
        EDIT_INGRESS_YAML: '/tenants/ingress/edit',
        SYNC_INGRESS: (tenant_key: string) => `/tenants/${tenant_key}/ingress/sync`,
        INGRESS_HISTORY: (tenant_key: string) => `/tenants/${tenant_key}/ingress/history`,
        // CloudFlare DNS Integration
        VIEW_CLOUDFLARE_DNS: '/tenants/cloudflare/dns/records',
        GET_TENANT_DNS: (tenant_key: string) => `/tenants/${tenant_key}/cloudflare/dns/records`,
        CREATE_DNS_RECORD: '/tenants/cloudflare/dns/records',
        UPDATE_DNS_RECORD: (record_id: string) => `/tenants/cloudflare/dns/records/${record_id}`,
        DELETE_DNS_RECORD: (record_id: string) => `/tenants/cloudflare/dns/records/${record_id}`,
        // Tenant Seeding
        SEED_TENANT: '/tenant-seed/seed',
    },

    // Tenant Reports — generated report records (management-scoped)
    TENANT_REPORTS: {
        GENERATE: '/tenant-reports/generate',
        LIST: '/tenant-reports',
        TENANTS: '/tenant-reports/tenants',   // lite { tenant_key, name } list for dropdown
        URL: (id: string) => `/tenant-reports/${id}/url`,        // public URL (View / open in browser)
        DOWNLOAD: (id: string) => `/tenant-reports/${id}/download`, // streamed blob (force save)
    },

    // AI Cost Analytics — cross-tenant cost dashboard (management-scoped)
    COST_ANALYTICS: {
        OVERVIEW: '/cost-analytics/overview',
        BY_TENANT: '/cost-analytics/by-tenant',
        BY_MODEL: '/cost-analytics/by-model',
        BY_CATEGORY: '/cost-analytics/by-category',
        BY_FEATURE: '/cost-analytics/by-feature',
        TREND: '/cost-analytics/trend',
        TREND_BY_TENANT: '/cost-analytics/trend-by-tenant',
        TENANT_DETAIL: '/cost-analytics/tenant-detail',
        EXPORT_PDF: '/cost-analytics/export-pdf',
        EXPORT_LOG: '/cost-analytics/export-log',
        RUN_ROLLUP: '/cost-analytics/rollup/run',
    },
    TENANT_USAGE: {
        SUMMARY: '/tenant-usage/summary',
        RECORDS: '/tenant-usage/records',
        TREND: '/tenant-usage/trend',
    },

    // Reviews endpoints - matches backend ReviewController
    REVIEWS: {
        // Learner endpoints
        LEARNER_CREATE: '/api/reviews/learner',
        LEARNER_MY_REVIEWS: '/api/reviews/learner/my-reviews',
        LEARNER_UPDATE: (id: string) => `/api/reviews/learner/${id}`,
        LEARNER_DELETE: (id: string) => `/api/reviews/learner/${id}`,
        LEARNER_HELPFUL: (id: string) => `/api/reviews/learner/${id}/helpful`,
        LEARNER_REPORT: (id: string) => `/api/reviews/learner/${id}/report`,

        // Admin endpoints
        ADMIN_LIST: '/api/reviews/admin',
        ADMIN_EXPORT: '/api/reviews/admin/export',
        ADMIN_METRICS: '/api/reviews/admin/metrics',
        ADMIN_GET: (id: string) => `/api/reviews/admin/${id}`,
        ADMIN_UPDATE_STATUS: (id: string) => `/api/reviews/admin/${id}/status`,
        ADMIN_RESPONSE: (id: string) => `/api/reviews/admin/${id}/response`,
        ADMIN_FEATURE: (id: string) => `/api/reviews/admin/${id}/feature`,
        ADMIN_DELETE: (id: string) => `/api/reviews/admin/${id}`,

        // Admin entity reviews (includes flagged/deleted)
        ADMIN_COURSE_REVIEWS: (courseId: string) => `/api/reviews/admin/course/${courseId}/reviews`,
        ADMIN_CERTIFICATION_REVIEWS: (certificationId: string) => `/api/reviews/admin/certification/${certificationId}/reviews`,
        ADMIN_LEARNING_PATH_REVIEWS: (learningPathId: string) => `/api/reviews/admin/learning-path/${learningPathId}/reviews`,

        // Public endpoints - Course
        COURSE_REVIEWS: (courseId: string) => `/api/reviews/course/${courseId}`,
        COURSE_STATS: (courseId: string) => `/api/reviews/course/${courseId}/stats`,

        // Public endpoints - Certification
        CERTIFICATION_REVIEWS: (certificationId: string) => `/api/reviews/certification/${certificationId}`,
        CERTIFICATION_STATS: (certificationId: string) => `/api/reviews/certification/${certificationId}/stats`,

        // Public endpoints - Learning Path
        LEARNING_PATH_REVIEWS: (learningPathId: string) => `/api/reviews/learning-path/${learningPathId}`,
        LEARNING_PATH_STATS: (learningPathId: string) => `/api/reviews/learning-path/${learningPathId}/stats`,

        // Bulk stats endpoint - for list pages (reduces N+1 API calls)
        BULK_STATS: '/api/reviews/bulk-stats',
    },

    // Email Template endpoints - matches backend EmailTemplateController
    EMAIL_TEMPLATES: {
        LIST: '/email-templates',
        CREATE: '/email-templates',
        GET_TYPES: '/email-templates/types',
        GET_BY_ID: (id: string) => `/email-templates/${id}`,
        GET_BY_KEY: (key: string) => `/email-templates/key/${key}`,
        GET_BY_TYPE: (type: string) => `/email-templates/type/${type}`,
        UPDATE: (id: string) => `/email-templates/${id}`,
        DELETE: (id: string) => `/email-templates/${id}`,
    },

    // Certificate Regeneration endpoints
    CERTIFICATE_REGENERATION: {
        // Admin endpoints
        ADMIN_CERTIFICATION: (certificateId: string) => `/certifications/certificates/${certificateId}/regenerate`,
        ADMIN_COURSE: (certificateId: string) => `/api/course-assignments/certificates/${certificateId}/regenerate`,
        ADMIN_LEARNING_PATH: (certificateId: string) => `/api/learning-path-assignments/certificates/${certificateId}/regenerate`,
        // Learner endpoints
        LEARNER_CERTIFICATION: (certificateId: string) => `/learner/certifications/certificates/${certificateId}/regenerate`,
        LEARNER_COURSE: (certificateId: string) => `/api/course-assignments/my-certificates/${certificateId}/regenerate`,
        LEARNER_LEARNING_PATH: (certificateId: string) => `/learner/learning-paths/certificates/${certificateId}/regenerate`,
    },

    // Certificate Template endpoints - matches backend CertificateTemplateController
    CERTIFICATE_TEMPLATES: {
        LIST: '/certificate-templates',
        CREATE: '/certificate-templates',
        GET_BY_ID: (id: string) => `/certificate-templates/${id}`,
        GET_BY_KEY: (key: string) => `/certificate-templates/key/${key}`,
        GET_BY_TYPE: (type: string) => `/certificate-templates/by-type/${type}`,
        GET_DEFAULT: (type: string) => `/certificate-templates/default/${type}`,
        GET_TEMPLATES_BY_TYPE: (type: string) => `/certificate-templates/type/${type}`,
        UPDATE: (id: string) => `/certificate-templates/${id}`,
        SET_DEFAULT: (id: string) => `/certificate-templates/${id}/set-default`,
        RESTORE: (id: string) => `/certificate-templates/${id}/restore`,
        DELETE: (id: string) => `/certificate-templates/${id}`,
    },

    // Export endpoints - matches backend ExportController
    EXPORT: {
        ACTIVITY_REPORT: '/api/admin/export/activity-report',
        USER_ANALYTICS: '/api/admin/export/user-analytics',
        GROUP_ANALYTICS: '/api/admin/export/group-analytics',
        ROLE_ANALYTICS: '/api/admin/export/role-analytics',
        GROUPS: '/api/admin/export/groups-export',
        ROLES: '/api/admin/export/roles-export',
        USERS_LIST: '/api/admin/export/list',
        INVITATIONS_LIST: '/api/admin/export/invitations/list',
        USER_COURSE_PROGRESS: (userId: string) => `/api/admin/export/user-course-progress/${userId}`,
        USER_CERTIFICATION_PROGRESS: (userId: string) => `/api/admin/export/user-certification-progress/${userId}`,
        CERTIFICATION_PERFORMANCE: '/api/admin/export/certification-performance',
        COURSE_PERFORMANCE: '/api/admin/export/course-performance',
        LEARNING_PATH_PERFORMANCE: '/api/admin/export/learning-path-performance',
        COURSE_USER_DATA: (courseId: string) => `/api/admin/export/course-user-data/${courseId}`,
        CERTIFICATION_USER_DATA: (certificationId: string) => `/api/admin/export/certification-user-data/${certificationId}`,
        LEARNING_PATH_USER_DATA: (learningPathId: string) => `/api/admin/export/learning-path-user-data/${learningPathId}`,
        APPROVER_REQUESTS: '/api/admin/export/approver-requests',
    },

    // Learning Path Workflow endpoints - matches backend LearningPathWorkflowController
    // Learning Paths - matches backend LearningPathsController
    LEARNING_PATHS: {
        LIST: '/learning-paths',
        GET_BY_ID: (id: string) => `/learning-paths/${id}`,
    },

    LEARNING_PATH_WORKFLOWS: {
        CREATE: '/learning-path-workflows',
        LIST: '/learning-path-workflows',
        GET_BY_ID: (id: string) => `/learning-path-workflows/${id}`,
        GET_STATUS: (id: string) => `/learning-path-workflows/${id}/status`,
        UPDATE: (id: string) => `/learning-path-workflows/${id}`,
        ACTIVATE: (id: string) => `/learning-path-workflows/${id}/activate`,
        DEACTIVATE: (id: string) => `/learning-path-workflows/${id}/deactivate`,
        METRICS: '/learning-path-workflows/metrics/dashboard',
    },

    // Audit Trail Report endpoints - matches backend AuditTrailReportController
    AUDIT_TRAIL: {
        LIST: '/audit-trail-report-srv/utils/list',
        GET_BY_ID: (reportId: string) => `/audit-trail-report-srv/${reportId}`,
        EVENT_TYPES: '/audit-trail-report-srv/utils/event-types',
        EVENT_DROPDOWN: '/audit-trail-report-srv/utils/event-dropdown',
        EXPORT: '/api/admin/export/audit-trail',
        EXPORT_CSV: '/api/admin/export/audit-trail-csv',
    },

    // Email Log endpoints - matches backend EmailLogController
    EMAIL_LOGS: {
        LIST: '/admin/email-logs/utils/list',
        GET_BY_ID: (id: string) => `/admin/email-logs/${id}`,
        METRICS: '/admin/email-logs/metrics',
        RETRY: (id: string) => `/admin/email-logs/${id}/retry`,
        BULK_RETRY: '/admin/email-logs/bulk-retry',
        FILTER_OPTIONS: '/admin/email-logs/filter-options',
    },

    // TTS Preview endpoints - matches backend TtsPreviewController
    TTS: {
        PREVIEW: '/api/tts/preview',
    },

    // Self Ticket Request endpoints - matches backend SelfTicketRequestController
    SELF_TICKET_REQUESTS: {
        // Create a new self-ticket request
        CREATE: '/api/self-ticket-requests',
        // List my requests (learner view)
        MY_LIST: '/api/self-ticket-requests/my/list',
        // Available items for self-ticket request (excludes already assigned)
        AVAILABLE_COURSES: '/api/self-ticket-requests/available/courses/list',
        AVAILABLE_CERTIFICATIONS: '/api/self-ticket-requests/available/certifications/list',
        AVAILABLE_LEARNING_PATHS: '/api/self-ticket-requests/available/learning-paths/list',
        // Assigned items (already assigned to the user)
        ASSIGNED_COURSES: '/api/self-ticket-requests/assigned/courses/list',
        ASSIGNED_LEARNING_PATHS: '/api/self-ticket-requests/assigned/learning-paths/list',
        // Approver endpoints (received requests)
        RECEIVED_LIST: '/api/self-ticket-requests/received/list',
        // Get request by ID
        GET_BY_ID: (id: string) => `/api/self-ticket-requests/${id}`,
        // Admin endpoints
        ADMIN_GET_BY_ID: (id: string) => `/api/self-ticket-requests/admin/${id}`,
        ADMIN_APPROVE: (id: string) => `/api/self-ticket-requests/${id}/approve`,
        ADMIN_DENY: (id: string) => `/api/self-ticket-requests/${id}/deny`,
        ADMIN_MARK_READ: (id: string) => `/api/self-ticket-requests/${id}/read`,
        // Learner mark messages as read
        LEARNER_MARK_READ: (id: string) => `/api/self-ticket-requests/${id}/read-messages`,
        // Stats endpoint (approver view)
        STATS: '/api/self-ticket-requests/stats',
        // Add message to ticket (chat)
        ADD_MESSAGE: (id: string) => `/api/self-ticket-requests/${id}/message`,
        // Delete message (WhatsApp style - POST with delete_type)
        DELETE_MESSAGE: (id: string, messageId: string) => `/api/self-ticket-requests/${id}/message/${messageId}/delete`,
        // Add reaction to message
        ADD_REACTION: (id: string, messageId: string) => `/api/self-ticket-requests/${id}/message/${messageId}/react`,
        // Remove reaction from message
        REMOVE_REACTION: (id: string, messageId: string) => `/api/self-ticket-requests/${id}/message/${messageId}/unreact`,
        // Clear chat (soft clear for current user)
        CLEAR_CHAT: (id: string) => `/api/self-ticket-requests/${id}/clear-chat`,
        // Close ticket (after approval or denial)
        CLOSE_TICKET: (id: string) => `/api/self-ticket-requests/${id}/close`,
        // Get approvers dropdown (unique BU Heads, RMs, HRBPs)
        APPROVERS_DROPDOWN: '/api/self-ticket-requests/approvers/dropdown',
    },

    // Notification endpoints - matches backend NotificationController
    NOTIFICATIONS: {
        // User-facing endpoints
        LIST: '/notifications',
        BELL_FEED: '/notifications/bell-feed',
        MARK_TOASTED: '/notifications/mark-toasted',
        UNREAD_COUNT: '/notifications/unread-count',
        MARK_READ: (id: string) => `/notifications/${id}/read`,
        MARK_ALL_READ: '/notifications/read-all',
        DISMISS: (id: string) => `/notifications/${id}`,
        CLEAR_POPUP: (id: string) => `/notifications/${id}/clear-popup`,

        // User preferences
        GET_PREFERENCES: '/notifications/preferences',
        UPDATE_PREFERENCES: '/notifications/preferences',

        // Push notification
        PUSH_CONFIG: '/notifications/push/config',
        PUSH_SUBSCRIBE: '/notifications/push/subscribe',
        PUSH_UNSUBSCRIBE: '/notifications/push/unsubscribe',
        PUSH_SYNC_STATE: '/notifications/push/sync-state',

        // Admin endpoints
        ADMIN: {
            SEND: '/admin/notifications',
            SCHEDULE: '/admin/notifications/schedule',
            HISTORY: '/admin/notifications/history',
            CANCEL: (id: string) => `/admin/notifications/${id}`,
            RETRY: (id: string) => `/admin/notifications/${id}/retry`,
            RECIPIENTS: (id: string) => `/admin/notifications/${id}/recipients`,
            METRICS: '/admin/notifications/metrics/summary',
            // Push subscribers (OneSignal is source of truth)
            PUSH_SUBSCRIBERS: '/admin/notifications/push/subscribers',
            PUSH_UNSUBSCRIBE: (user_id: string) => `/admin/notifications/push/subscribers/${user_id}`,
            PUSH_UNSUBSCRIBE_SUBSCRIPTION: (player_id: string) => `/admin/notifications/push/subscriptions/${player_id}`,

            // System notification configuration
            SYSTEM_CONFIG_LIST: '/admin/notifications/system-config/events',
            SYSTEM_CONFIG_EVENT: (event_type: string) => `/admin/notifications/system-config/events/${event_type}`,
            SYSTEM_CONFIG_BULK: '/admin/notifications/system-config/events',
        },
    },
};
